// @flow
import React, { useContext, useEffect, useRef } from 'react';
import { FormProvider, useWatch } from 'react-hook-form';
import { FormattedMessage } from 'react-intl';
import { useFlags } from 'launchdarkly-react-client-sdk';
import {
  Box,
  Button,
  Stack,
  Typography,
} from '@fortress-technology-solutions/fortress-component-library/Atoms';
import ElementWithPermissions from '../../../components/ElementWithPermissions';
import { RenewalOfferContext } from '../context';
import messages from '../messages';
import { useRenewalOfferForm } from './hooks';
import { MAX_OFFERS } from './helpers';
import ExpirationDateField from './ExpirationDateField';
import OfferTermsRow from './OfferTermsRow';
import OfferRentRow from './OfferRentRow';
import TransactionList from './TransactionList';
import RenderTermLength from './TermLengthField';
import RenderCost from './CostField';
import TransactionRow from './TransactionRow';

export { RenderTermLength, RenderCost, TransactionRow };

const RenewalOfferForm = ({ onSubmit, termsDropDown }) => {
  const {
    rebaPricingByTermId,
    rebaProviderNameByTermId,
    quoteExpTime,
    isPartnerEnabled,
  } = useContext(RenewalOfferContext);
  const { renewalOfferLetterRows, rebaRevMgmt } = useFlags();

  // ENG-31380 — the ONE place this condition is decided: the read-only, server-authoritative
  // expiration needs the flag AND a provider actually set up for the property. Derived once and
  // handed to both consumers — the field and the form's defaultValues/resolver — so they cannot
  // drift apart. Read before useRenewalOfferForm, which needs it at initialization time.
  const isServerAuthoritative = Boolean(rebaRevMgmt && isPartnerEnabled);

  const {
    methods,
    fields,
    addTransaction,
    deleteTransaction,
    handleFormSubmit,
  } = useRenewalOfferForm({
    onSubmit,
    quoteExpTime,
    isServerAuthoritative,
  });

  const {
    control,
    setValue,
    formState: { isValid },
  } = methods;

  const offerNames = Array.from(
    { length: MAX_OFFERS },
    (_, i) => i + 1,
  ).flatMap((n) => [`offer${n}Rent`, `offer${n}TermId`]);
  const offerWatched = useWatch({ control, name: offerNames });
  const offerData = Array.from({ length: MAX_OFFERS }, (_, i) => ({
    rent: offerWatched[i * 2],
    termId: offerWatched[i * 2 + 1],
  }));

  const disableTermFlags = Array.from({ length: MAX_OFFERS }, (_, i) => {
    if (i === 0) return false;
    const prev = offerData[i - 1];
    const prevDisabled = !prev.rent || !prev.termId || +prev.rent <= 0;
    if (i === MAX_OFFERS - 1) return prevDisabled && !offerData[i].termId;
    return prevDisabled;
  });

  // ENG-30334 — REBA's rent for the term this tier currently has selected, or undefined when
  // REBA does not own it. Keyed off the SELECTED term, so changing the dropdown re-resolves the
  // tier: a 12-month price must not linger on a tier the user just switched to 6 months.
  const rebaRentByTier = Array.from({ length: MAX_OFFERS }, (_, i) =>
    rebaRevMgmt ? rebaPricingByTermId?.[offerData[i].termId] : undefined,
  );

  const rebaProviderNameByTier = Array.from({ length: MAX_OFFERS }, (_, i) =>
    rebaRevMgmt
      ? rebaProviderNameByTermId?.[offerData[i].termId] ?? null
      : null,
  );

  // What REBA last wrote into each tier, so a tier that LOSES REBA ownership can be cleared
  // without ever discarding something the user typed themselves.
  const rebaWrittenRef = useRef({});

  // Pre-fill each REBA-owned tier, and clear a tier that stops being REBA-owned. Writing on
  // every resolved change (rather than once on mount) is what makes a term switch re-price the
  // tier; `setValue` is a no-op when the value already matches, so this does not fight the user
  // on unrelated re-renders.
  //
  // The clear half is not symmetry for its own sake. Without it, switching a tier from a
  // REBA-priced term to one REBA does not price left the OLD term's rent sitting in the field,
  // which then became editable — a figure the user never typed, presented as if they had. And
  // the server could not correct it: the save path only overwrites tiers REBA owns, so by
  // definition it leaves that tier alone. A 12-month price would have been saved, and printed
  // into the renewal-offer letter, as the 6-month rent.
  //
  // Clearing is deliberately conditional on the field still holding REBA's own last value. If
  // the user has since typed over it, that is their figure and it stays.
  //
  // The rest of this is presentation only. The API re-resolves pricing on save and overwrites
  // any tier REBA owns, so a stale value here — or a hand-edited one, or a replayed request —
  // cannot become the rent for a tier REBA prices. Disabling the input is a courtesy; the
  // server is the guarantee.
  useEffect(() => {
    rebaRentByTier.forEach((rent, i) => {
      const fieldName = `offer${i + 1}Rent`;

      if (rent !== undefined && rent !== null) {
        const formatted = Number(rent).toFixed(2);
        if (offerData[i].rent !== formatted) {
          setValue(fieldName, formatted, {
            shouldValidate: true,
            shouldDirty: true,
          });
        }
        rebaWrittenRef.current[fieldName] = formatted;
        return;
      }

      const lastWritten = rebaWrittenRef.current[fieldName];
      if (lastWritten !== undefined && offerData[i].rent === lastWritten) {
        setValue(fieldName, '', { shouldValidate: true, shouldDirty: true });
      }
      delete rebaWrittenRef.current[fieldName];
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [JSON.stringify(rebaRentByTier)]);

  const disableRentFlags = Array.from({ length: MAX_OFFERS }, (_, i) => {
    // A tier REBA owns is read-only regardless of the cascade below: the figure is not the
    // user's to choose. Checked first so it cannot be re-enabled by the ordering rules.
    if (rebaRentByTier[i] !== undefined && rebaRentByTier[i] !== null)
      return true;
    if (i === 0) return false;
    const prev = offerData[i - 1];
    const prevDisabled = !prev.rent || !prev.termId || +prev.rent <= 0;
    if (i === MAX_OFFERS - 1) return prevDisabled && !offerData[i].rent;
    return prevDisabled;
  });

  return (
    <FormProvider {...methods}>
      <Box
        className="panel unit-information panel-withheader"
        data-test="renewal-offer-form"
      >
        <Box className="panel-head" sx={{ p: 2 }}>
          <Stack
            flexDirection="row"
            alignItems="center"
            justifyContent="space-between"
          >
            <Typography variant="titleMedium">
              <FormattedMessage {...messages.newRenewalOffer} />
            </Typography>
            <Stack flexDirection="row" alignItems="center" gap={1}>
              <ExpirationDateField
                isServerAuthoritative={isServerAuthoritative}
              />
            </Stack>
          </Stack>
        </Box>
        <Box sx={{ p: 2 }}>
          <Stack flexDirection="row" alignItems="center" gap={2} sx={{ mb: 2 }}>
            <Box sx={{ width: '25%' }}>
              <Typography variant="titleSmall">
                <FormattedMessage {...messages.requiredTerm} />
              </Typography>
            </Box>
            <OfferTermsRow
              termsDropDown={termsDropDown}
              disableFlags={disableTermFlags}
            />
          </Stack>
          <Stack flexDirection="row" alignItems="center" gap={2} sx={{ mb: 2 }}>
            <Box sx={{ width: '25%' }}>
              <Typography variant="titleSmall">
                <FormattedMessage {...messages.requiredLeaseRent} />
              </Typography>
            </Box>
            <OfferRentRow
              disableFlags={disableRentFlags}
              providerNames={rebaProviderNameByTier}
            />
          </Stack>
          {renewalOfferLetterRows && (
            <TransactionList
              fields={fields}
              addTransaction={addTransaction}
              deleteTransaction={deleteTransaction}
            />
          )}
          <Box sx={{ mt: 2 }}>
            <Typography variant="bodyMedium">
              <strong>
                <FormattedMessage {...messages.note} />
                &nbsp;
              </strong>
              <FormattedMessage {...messages.noteText} />
            </Typography>
          </Box>
          <Box sx={{ display: 'flex', justifyContent: 'center', mt: 2 }}>
            <ElementWithPermissions scope={['lease-create']}>
              <Button
                variant="primary"
                type="button"
                onClick={handleFormSubmit}
                disabled={!isValid}
              >
                <FormattedMessage {...messages.generateRenewalOffer} />
              </Button>
            </ElementWithPermissions>
          </Box>
        </Box>
      </Box>
    </FormProvider>
  );
};

export { RenewalOfferForm };
export default RenewalOfferForm;
